// The trip pack: everything trip-specific (places, dates, copy, colours, cards).
// A second trip is a new pack folder, not a code change. See docs/HANDOVER.md §2, §9.
import { z } from 'zod'
import { RewardMachineId } from '../rewards'

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
const isoDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?[+-]\d{2}:\d{2}$/, 'ISO with offset')
const latLng = z.tuple([z.number().min(-90).max(90), z.number().min(-180).max(180)])
const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'kebab-case id')
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)

// ---------- meta ----------

export const WeatherCondition = z.enum(['snow', 'clear', 'cloudy', 'rain'])
export type WeatherCondition = z.infer<typeof WeatherCondition>

export const Climate = z.object({ tempC: z.number(), condition: WeatherCondition })
export type Climate = z.infer<typeof Climate>

/**
 * A fact a grown-up adds about the trip (B6), asked by Explorer as a card: the question, the right
 * answer and up to three wrong ones (none: the other facts' answers stand in).
 */
export const TripFact = z.object({
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  q: z.string().trim().min(1).max(140),
  a: z.string().trim().min(1).max(60),
  wrong: z.array(z.string().trim().min(1).max(60)).max(3).optional(),
})
export type TripFact = z.infer<typeof TripFact>

export const TripMeta = z.object({
  id,
  version: z.number().int().positive(),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  timezone: z.string().min(1),
  startDate: isoDate,
  endDate: isoDate,
  /** Region id → month ('01'..'12') → typical weather; the fallback when live weather is stale. */
  climatology: z.record(z.string(), z.record(z.string().regex(/^(0[1-9]|1[0-2])$/), Climate)),
  /** Typical sunrise/sunset in the trip timezone (drives the Map's day/dusk/night). */
  daylight: z.object({ sunrise: z.string().regex(/^\d{2}:\d{2}$/), sunset: z.string().regex(/^\d{2}:\d{2}$/) }),
  /** The language kit's locale, for speech (D3). */
  locale: z.string().min(2).optional(),
  /**
   * The language kit's script decks, in teaching order (katakana, hiragana; hangul): each is a
   * Learn track (WI-10). A pack built before this field derives them (src/domain/kana `scriptDecks`).
   */
  scripts: z.array(z.string()).optional(),
  /** The language kit's name for its script in the reading machine's title ("Kana", "Hangul"). */
  readName: z.string().min(1).optional(),
  /** The region kit the trip is built on (`elsewhere` for none), so it can be copied (B9). */
  kit: z.string().optional(),
  /** The region kit's reward machine (B16), the default for this trip. */
  reward: z.object({ machine: RewardMachineId }).optional(),
  /** The parent's choice of reward machine for this trip, over the kit's (trip.meta.set). */
  rewardMachine: RewardMachineId.optional(),
  /** The region kit's local money (ISO 4217), for Number Rush and prices. */
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  /**
   * Who's coming (B9): the ids of the children on this trip. Absent: every child. A child not on
   * it sees the trip as a read-only "family trip" (src/domain/trip/travellers); grown-ups always see it.
   */
  travellers: z.array(z.string().min(1)).optional(),
  /** Facts a grown-up adds about the trip (B6): Explorer asks them as cards (trip.meta.set). */
  facts: z.array(TripFact).max(40).optional(),
})
export type TripMeta = z.infer<typeof TripMeta>

// ---------- theme ----------

export const Theme = z.object({
  colors: z.record(z.string().regex(/^[a-z][a-z0-9-]*$/), hex),
})
export type Theme = z.infer<typeof Theme>

// ---------- itinerary ----------

export const StageColor = z.enum(['blue', 'snow', 'teal', 'mint', 'yellow', 'red'])

/** Hidden from children until `until` (a date, or a date and time in the trip timezone); they see `teaser`. */
export const Surprise = z.object({
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/),
  teaser: z.string().min(1).max(80),
  /** An optional hint shown with the countdown ("Glass, canals and a steam clock"). */
  clue: z.string().max(120).optional(),
})
export type Surprise = z.infer<typeof Surprise>

export const Stage = z.object({
  id,
  regionId: id,
  name: z.string().min(1),
  shortName: z.string().min(1),
  startDate: isoDate,
  endDate: isoDate,
  anchor: latLng,
  color: StageColor,
  /** Where the stage's pin sits on the Map island (one of the island's named spots). */
  spot: id,
  surprise: Surprise.optional(),
  /** The stage's country (ISO 3166-1 alpha-2), from the place lookup (B3): picks the island list. */
  country: z.string().regex(/^[A-Z]{2}$/).optional(),
  /**
   * The stage's own time zone (IANA), when it isn't the trip's (B13: Japan then Korea). While the
   * stage is under way its clocks, reveals and play-day use it (shared/tripZone). None: the trip's.
   */
  timezone: z.string().min(1).max(64).optional(),
})
export type Stage = z.infer<typeof Stage>

export const LegKind = z.enum(['flight', 'train', 'bus', 'lift', 'car', 'walk', 'ferry'])
export type LegKind = z.infer<typeof LegKind>

const Stop = z.object({ code: z.string().min(1), name: z.string().min(1), at: latLng })

export const Leg = z.object({
  id,
  kind: LegKind,
  regionId: id,
  from: Stop,
  to: Stop,
  departsAt: isoDateTime,
  arrivesAt: isoDateTime,
  ref: z.string().optional(),
  vehicle: z.string().optional(),
  notes: z.string().optional(),
  /** Tickets bought (the mint BOOKED tag). Absent: booked only when it has a booking ref (legBooked). */
  booked: z.boolean().optional(),
  /**
   * No times yet (WI-42: a "How do you get there?" chip with the times left for later): the
   * departure sits at midday that day so it sorts and lands on the right day, and no screen shows a
   * clock for it. Written times clear it.
   */
  untimed: z.boolean().optional(),
})
export type Leg = z.infer<typeof Leg>

export const DayItem = z.object({
  id,
  date: isoDate,
  time: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  endTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  title: z.string().min(1),
  sub: z.string().optional(),
  placeId: id.optional(),
  booked: z.boolean(),
  /** Children can see it but not move or hide it. Booked items are locked unless said otherwise. */
  locked: z.boolean().optional(),
  surprise: Surprise.optional(),
  /** Who's going (person ids); everyone when absent. */
  who: z.array(z.string()).optional(),
  /** Plays a child earns when a grown-up confirms it's done. */
  award: z.object({ plays: z.number().int().min(1).max(5) }).optional(),
  /** Who added it, for items made in the app. */
  by: z.string().optional(),
})
export type DayItem = z.infer<typeof DayItem>

export const Itinerary = z.object({
  stages: z.array(Stage).min(1),
  legs: z.array(Leg),
  /** Pack-authored plans. Most day items are made in the app as events instead. */
  dayItems: z.array(DayItem),
})
export type Itinerary = z.infer<typeof Itinerary>

// ---------- places ----------

export const PlaceKind = z.enum(['food', 'onsen', 'snow', 'poi', 'shop', 'transit', 'stay', 'activity'])
export type PlaceKind = z.infer<typeof PlaceKind>

export const Place = z.object({
  id,
  name: z.string().min(1),
  nameJa: z.string().optional(),
  kind: PlaceKind,
  regionId: id,
  at: latLng,
  summary: z.string().min(1),
  tips: z.array(z.string()),
  tags: z.array(z.string()),
  /** The place's sticker (Phase 6 art, S5): a 256 px WebP relative to the pack, once the owner picks. */
  sticker: z.string().optional(),
})
export type Place = z.infer<typeof Place>

// ---------- decks ----------

/**
 * A card header picture (a 288 px WebP). In a kit's deck JSON it's relative to the kit's folder
 * (`art/word-strawberry.webp`, a file in kits/<kit>/art/); the pack build serves each kit's art at
 * /packs/art/<kit>/ and rewrites the path to that URL (scripts/lib/pack-dir.ts), so a built pack and
 * a kit bundle both carry URLs the app shows as they are.
 */
const art = z.string().min(1)

/** A deck's context: where you'd meet its cards ("At the till"), optionally with its situation picture. */
/** A Learn machine a deck feeds (Deck.learn). */
export const LearnUse = z.enum(['speak', 'read', 'numbers'])
export type LearnUse = z.infer<typeof LearnUse>

export const DeckContext = z.union([z.string().min(1), z.object({ label: z.string().min(1), art: art.optional() })])
export type DeckContext = z.infer<typeof DeckContext>

const cardBase = {
  id,
  xp: z.number().int().positive(),
  tags: z.array(z.string()),
  /** Japanese text for TTS when no audio file is available. */
  say: z.string().optional(),
  anecdote: z.string().optional(),
  /** Where you'd see this in the wild ("On the menu board outside the shop"). */
  context: z.string().optional(),
  /** The card's own header picture (a script card: its example word's memory picture); else its context's. */
  art: art.optional(),
}

export const ReadingCard = z.object({
  ...cardBase,
  kind: z.literal('reading'),
  script: z.string().min(1),
  reading: z.string().min(1),
  meaning: z.string().min(1),
})
export const McqCard = z.object({
  ...cardBase,
  kind: z.literal('mcq'),
  prompt: z.string().min(1),
  options: z.array(z.string().min(1)).min(2),
  answer: z.number().int().nonnegative(),
})
export const SpeakCard = z.object({
  ...cardBase,
  kind: z.literal('speak'),
  prompt: z.string().min(1),
  target: z.string().min(1),
  threshold: z.number().min(0).max(1),
  /** What it means, in the app's language ("please (polite)"): the phrase quiz asks for it. */
  meaning: z.string().min(1).optional(),
})
export const ListenCard = z.object({
  ...cardBase,
  kind: z.literal('listen'),
  answer: z.string().min(1),
  alts: z.array(z.string()),
})
export const RecallCard = z.object({
  ...cardBase,
  kind: z.literal('recall'),
  prompt: z.string().min(1),
  answer: z.string().min(1),
  alts: z.array(z.string()),
  /** What it means ("how much is it?"): the phrase quiz asks for it. */
  meaning: z.string().min(1).optional(),
})
export const TaskCard = z.object({
  ...cardBase,
  kind: z.literal('task'),
  brief: z.string().min(1),
  proofHint: z.string().min(1),
  unlock: z.object({ regionId: id }).optional(),
})

export const Card = z.discriminatedUnion('kind', [ReadingCard, McqCard, SpeakCard, ListenCard, RecallCard, TaskCard])
export type Card = z.infer<typeof Card>

export const Deck = z.object({
  id,
  title: z.string().min(1),
  kind: z.string().min(1),
  locale: z.string().min(2),
  order: z.number().int(),
  unlock: z.object({ afterDeckId: id }).optional(),
  /** Card tag → where you'd see it ("At the till", or { label, art } with its picture); the first matching tag wins. */
  contexts: z.record(z.string(), DeckContext).optional(),
  /** Groups of easily-confused glyphs, used for wrong options (katakana: シ ツ ン ソ…). */
  lookalikes: z.array(z.array(z.string()).min(2)).optional(),
  /**
   * The Learn machines this deck feeds (B7: the kit says, the code names no deck): `speak` =
   * Speak Up's phrases and listening (Learn's everyday words), `read` = the reading machine's real
   * words and sign questions (Learn's "together"), `numbers` = Number Rush. A script deck (the
   * language kit's `scripts`) needs none: it's a script track of its own.
   */
  learn: z.array(LearnUse).optional(),
  cards: z.array(Card).min(1),
})
export type Deck = z.infer<typeof Deck>

// ---------- the whole pack ----------

export const Pack = z
  .object({
    meta: TripMeta,
    theme: Theme,
    itinerary: Itinerary,
    places: z.array(Place),
    decks: z.array(Deck),
  })
  .superRefine((pack, ctx) => {
    const dupes = (label: string, ids: string[]) => {
      const seen = new Set<string>()
      for (const i of ids) {
        if (seen.has(i)) ctx.addIssue({ code: 'custom', message: `duplicate ${label} id: ${i}` })
        seen.add(i)
      }
    }
    dupes('stage', pack.itinerary.stages.map((s) => s.id))
    dupes('leg', pack.itinerary.legs.map((l) => l.id))
    dupes('place', pack.places.map((p) => p.id))
    dupes('deck', pack.decks.map((d) => d.id))
    dupes('card', pack.decks.flatMap((d) => d.cards.map((c) => c.id)))

    const stages = pack.itinerary.stages
    if (stages[0]?.startDate !== pack.meta.startDate) {
      ctx.addIssue({ code: 'custom', message: 'first stage must start on meta.startDate' })
    }
    if (stages.at(-1)?.endDate !== pack.meta.endDate) {
      ctx.addIssue({ code: 'custom', message: 'last stage must end on meta.endDate' })
    }
    stages.forEach((s, i) => {
      if (s.endDate < s.startDate) ctx.addIssue({ code: 'custom', message: `stage ${s.id} ends before it starts` })
      const next = stages[i + 1]
      if (next && addDays(s.endDate, 1) !== next.startDate) {
        ctx.addIssue({ code: 'custom', message: `stages ${s.id} → ${next.id} are not contiguous` })
      }
    })

    for (const d of pack.decks) {
      for (const c of d.cards) {
        if (c.kind === 'mcq' && c.answer >= c.options.length) {
          ctx.addIssue({ code: 'custom', message: `card ${c.id}: answer index out of range` })
        }
      }
      if (d.unlock && !pack.decks.some((o) => o.id === d.unlock?.afterDeckId)) {
        ctx.addIssue({ code: 'custom', message: `deck ${d.id} unlocks after unknown deck` })
      }
    }
    for (const di of pack.itinerary.dayItems) {
      if (di.placeId && !pack.places.some((p) => p.id === di.placeId)) {
        ctx.addIssue({ code: 'custom', message: `day item ${di.id} points at unknown place` })
      }
    }
  })
export type Pack = z.infer<typeof Pack>

function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
